import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { Help, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService } from "../../features/gamification/service";
import type { GrantFilter, GrantRow, Paged, Quest } from "../../features/gamification/types";

import { InfoTh, LoadError, Pager, PersonCell, StatusBadge, dash, tdCls, thCls } from "./gamificationShared";

// ── Who got what ─────────────────────────────────────────────────────────────
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A date input (India calendar day) as the instant the day starts / ends. */
function dayBound(day: string, end: boolean): string | undefined {
  if (!ISO_DAY.test(day)) return undefined;
  return `${day}T${end ? "23:59:59" : "00:00:00"}+05:30`;
}

export function GrantsPanel({ quests }: { quests: Quest[] }) {
  const toast = useAdminToast();
  const [form, setForm] = useState({ quest_id: "", status: "", role: "", from: "", to: "", q: "" });
  const [filter, setFilter] = useState<GrantFilter>({});
  const [data, setData] = useState<Paged<GrantRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (f: GrantFilter, page: number) => {
    setLoading(true);
    try {
      setData(await gamificationService.listGrants(f, page));
      setError("");
    } catch (e) {
      setData(null);
      setError(errMsg(e, "Failed to load grants"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load({}, 1); }, [load]);

  const apply = () => {
    const f: GrantFilter = {
      quest_id: form.quest_id, status: form.status, role: form.role, q: form.q,
      date_from: dayBound(form.from, false), date_to: dayBound(form.to, true),
    };
    setFilter(f);
    void load(f, 1);
  };
  const reset = () => {
    setForm({ quest_id: "", status: "", role: "", from: "", to: "", q: "" });
    setFilter({});
    void load({}, 1);
  };
  const exportCsv = async () => {
    setExporting(true);
    try {
      await gamificationService.exportGrants(filter);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to export grants"));
    } finally {
      setExporting(false);
    }
  };
  const set = (p: Partial<typeof form>) => setForm((x) => ({ ...x, ...p }));

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Who got what</h2>
      <p className="text-gray-500 text-xs mb-4">
        Every reward paid, newest first. Filter, then download the same rows as a CSV (up to 10,000).
        <span className="block text-gray-600">Example: filter by a quest and a date range to see who was paid for it last week.</span>
      </p>
      <p className="text-gray-400 text-xs mb-4">Every reward given to a person. Status granted = credits are in the wallet. Expires = when these free credits stop working.</p>
      <form className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-3" onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <div>
          <label className={labelCls} htmlFor="gg-quest">Quest</label>
          <select id="gg-quest" className={inputCls} value={form.quest_id} onChange={(e) => set({ quest_id: e.target.value })}>
            <option value="">All quests</option>
            {quests.map((q) => <option key={q.quest_id} value={q.quest_id}>{q.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gg-status">Status</label>
          <select id="gg-status" className={inputCls} value={form.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">Any</option>
            <option value="granted">Granted</option>
            <option value="pending">Pending</option>
            <option value="reversed">Taken back</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gg-role">Role</label>
          <select id="gg-role" className={inputCls} value={form.role} onChange={(e) => set({ role: e.target.value })}>
            <option value="">Any</option>
            <option value="seller">Seller</option>
            <option value="buyer">Buyer</option>
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="gg-from">From (IST)</label>
          <input id="gg-from" type="date" className={inputCls} value={form.from} onChange={(e) => set({ from: e.target.value })} />
        </div>
        <div>
          <label className={labelCls} htmlFor="gg-to">To (IST)</label>
          <input id="gg-to" type="date" className={inputCls} value={form.to} onChange={(e) => set({ to: e.target.value })} />
        </div>
        <div>
          <label className={labelCls} htmlFor="gg-q">User</label>
          <input id="gg-q" className={inputCls} value={form.q} onChange={(e) => set({ q: e.target.value })} placeholder="Name, email, phone or id" />
        </div>
        <div className="sm:col-span-3 lg:col-span-6 flex flex-wrap gap-2">
          <button type="submit" className={btnPrimary} disabled={loading}>Apply filters</button>
          <button type="button" className={btnGhost} onClick={reset} disabled={loading}>Clear</button>
          <button type="button" className={btnGhost} onClick={exportCsv} disabled={exporting}>{exporting ? "Preparing…" : "Download CSV"}</button>
        </div>
      </form>
      <Help>The date range can be at most 366 days. A user search needs at least 3 characters.</Help>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => void load(filter, 1)} />
      ) : data.items.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No grants match these filters.</p>
      ) : (
        <>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className={thCls}>User</th>
                  <th className={thCls}>Role</th>
                  <th className={thCls}>Quest</th>
                  <th className={thCls}>Credits</th>
                  <InfoTh label="Status" tip="Granted = the credits are in the person's wallet. Pending = waiting (for example a review). Reversed = taken back." />
                  <th className={thCls}>Created</th>
                  <InfoTh last label="Expires" tip="When these free credits stop working if the person has not used them." />
                </tr>
              </thead>
              <tbody>
                {data.items.map((g) => (
                  <tr key={g.grant_key} className="border-t border-gray-800 text-gray-300">
                    <td className={tdCls}>
                      <PersonCell id={g.user_id} name={g.user_name} email={g.user_email} phone={g.user_phone} />
                    </td>
                    <td className={`${tdCls} capitalize`}>{dash(g.role)}</td>
                    <td className={tdCls}>{g.quest_name || g.quest_id || "—"}</td>
                    <td className={tdCls}>{g.credits}</td>
                    <td className={tdCls}><StatusBadge status={g.status} />{!g.ledger_ok && g.status === "granted" && <span className="text-amber-400 text-[11px] ml-1">history row missing</span>}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatISTDateTime(g.created_at)}</td>
                    <td className="py-2 whitespace-nowrap">{formatISTDateTime(g.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={data} busy={loading} onPage={(p) => void load(filter, p)} />
        </>
      )}
    </section>
  );
}
